// PhotobookPage - Updated with html2canvas PDF generation v2.0
import React, { useState, useEffect, useRef } from "react";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import {
  ArrowBack,
  GetApp,
  PhotoLibrary,
} from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { ThemeProvider } from "@emotion/react";
import { createTheme } from "@mui/material/styles";
import axios from "axios";
import { baseUrl, config } from "../../common/utils";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

// Theme matching AdminDashboard
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

interface Student {
  id: string;
  studentNumber: string;
  firstname: string;
  lastname: string;
  othername?: string;
  gender: string;
  accCode: string;
  passport?: string;
  passportBase64?: string; // Add base64 image data
  dateOfBirth?: string; // Added for DOB display
}

const PhotobookPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [schoolName, setSchoolName] = useState<string>("");
  const [schoolCode, setSchoolCode] = useState<string>("");
  const [lgaCode, setLgaCode] = useState<string>("");
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const schCode = params.get('schoolCode');
    const lga = params.get('lgaCode');
    const schName = params.get('schoolName');

    if (schCode && lga) {
      setSchoolCode(schCode);
      setLgaCode(lga);
      setSchoolName(schName || 'School');
      fetchStudents(schCode, lga);
    } else {
      setError("Missing school information");
      setLoading(false);
    }
  }, [location]);

  const fetchStudents = async (schCode: string, lga: string) => {
    try {
      const res = await axios.post(
        `${baseUrl()}Admin2s/studentsBySchool_lga`,
        {
          schCode: schCode,
          lgaCode: lga,
        },
        {
          ...config,
          headers: {
            ...config.headers,
            'Content-Type': 'application/json'
          }
        }
      );

      if (res.data.status && res.data.data) {
        const rawData = res.data.data;
        const processedStudents: Student[] = [];
        
        // Process in chunks of 5 to avoid overloading network/memory
        const chunkSize = 5;
        for (let i = 0; i < rawData.length; i += chunkSize) {
          const chunk = rawData.slice(i, i + chunkSize);
          const chunkResults = await Promise.all(
            chunk.map(async (student: Student) => {
              // 1. Check if passport is already base64
              if (student.passport && student.passport.startsWith('data:')) {
                return { ...student, passportBase64: student.passport };
              }

              // 2. Otherwise fetch the image
              try {
                const passportPath = student.passport?.trim();
                const imageUrl = `${baseUrl()}assets${
                  passportPath 
                    ? (passportPath.startsWith('/') ? passportPath : `/${passportPath}`)
                    : `/img/students/${student.studentNumber}.jpg`
                }`;

                // Use fetch with authentication headers
                const imageResponse = await fetch(imageUrl, {
                  headers: config.headers
                });

                if (!imageResponse.ok) {
                  console.warn(`Image fetch failed for ${student.studentNumber}: ${imageResponse.status}`);
                  return student;
                }

                const blob = await imageResponse.blob();
                const base64 = await new Promise<string>((resolve, reject) => {
                  const img = new Image();
                  img.crossOrigin = "anonymous";
                  
                  const timeout = setTimeout(() => {
                    reject(new Error("Image load timeout"));
                  }, 10000);

                  img.onload = () => {
                    clearTimeout(timeout);
                    const canvas = document.createElement('canvas');
                    // Resize large images to save memory in PDF
                    const maxDim = 400;
                    let width = img.width;
                    let height = img.height;
                    
                    if (width > maxDim || height > maxDim) {
                      if (width > height) {
                        height = (height / width) * maxDim;
                        width = maxDim;
                      } else {
                        width = (width / height) * maxDim;
                        height = maxDim;
                      }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.7));
                    URL.revokeObjectURL(img.src);
                  };

                  img.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error("Image load error"));
                  };

                  img.src = URL.createObjectURL(blob);
                });

                return { ...student, passportBase64: base64 };
              } catch (error) {
                console.warn(`Failed to process image for student ${student.studentNumber}:`, error);
                return student;
              }
            })
          );
          processedStudents.push(...chunkResults);
          // Small delay between chunks to let the UI breathe
          if (i + chunkSize < rawData.length) {
             setStudents([...processedStudents]); // Update UI incrementally
          }
        }
        setStudents(processedStudents);
      } else {
        setError("Failed to fetch students");
      }
    } catch (err) {
      console.error("Error fetching students:", err);
      setError("Error loading students");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.push("/student_list");
  };

  const downloadPDF = async () => {
    console.log('=== GENERATING PDF ROW BY ROW ===');
    if (!printRef.current) return;

    try {
      // Use the constructor for jsPDF
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
       const margin = 12; // Reduced margin
      const rowSpacing = 3;
      const colSpacing = 3;
      const cardsPerRow = 4;
      const cardsPerCol = 4; // Target 4 rows per page
      const cardWidth = (pdfWidth - margin * 2 - colSpacing * (cardsPerRow - 1)) / cardsPerRow;
      
      // Calculate cardHeight based on available space to fit 4 rows
      // Total height = 297. Header takes ~35mm. Bottom margin 12mm.
      // 297 - 35 - 12 = 250. 250 / 4 = 62.5. 
      const cardHeight = 58; 
      const imageSize = 34; // Reduced to fit more text below
      let yPosition = margin;
      let rowsOnPage = 0;

      // Add header on first page
      pdf.setFontSize(16);
      pdf.text(`${schoolName} - Student Photobook`, pdfWidth / 2, yPosition, { align: 'center' });
      yPosition += 12;

      pdf.setFontSize(10);
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pdfWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Process students in rows
      for (let i = 0; i < students.length; i += cardsPerRow) {
        const rowStudents = students.slice(i, i + cardsPerRow);

        // Start a new page if the next row would exceed page height or if 4 rows are already on page
        if (rowsOnPage === 4 || yPosition + cardHeight > pdfHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          rowsOnPage = 0;

          // Add header on each new page too
          pdf.setFontSize(16);
          pdf.text(`${schoolName} - Student Photobook`, pdfWidth / 2, yPosition, { align: 'center' });
          yPosition += 12;
          pdf.setFontSize(10);
          pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pdfWidth / 2, yPosition, { align: 'center' });
          yPosition += 15;
        }

        // Render each card in the row
        for (let j = 0; j < rowStudents.length; j++) {
          const student = rowStudents[j];
          const xPosition = margin + j * (cardWidth + colSpacing);

          // Add image
          if (student.passportBase64) {
            try {
              pdf.addImage(student.passportBase64, 'JPEG', xPosition + 5, yPosition, imageSize, imageSize);
            } catch (err) {
              console.warn(`Failed to add image for ${student.studentNumber}`);
            }
          }

           // Add text below image - Compact layout
          const textStartY = yPosition + imageSize + 3;
          pdf.setFontSize(7.5);
          pdf.setFont(undefined, 'bold');
          pdf.text(`#${student.studentNumber}`, xPosition + cardWidth / 2, textStartY, { align: 'center' });
          
          pdf.setFont(undefined, 'normal');
          pdf.setFontSize(6.5);
          const nameText = `${student.lastname} ${student.firstname}`;
          pdf.text(nameText.toUpperCase(), xPosition + cardWidth / 2, textStartY + 3.5, { align: 'center', maxWidth: cardWidth - 4 });
          
          pdf.setFontSize(6);
          let detailY = textStartY + 7;
          pdf.text(`${student.gender}`, xPosition + cardWidth / 2, detailY, { align: 'center' });
          
          if (student.dateOfBirth) {
            const dob = new Date(student.dateOfBirth).toLocaleDateString();
            detailY += 3;
            pdf.text(`DOB: ${dob}`, xPosition + cardWidth / 2, detailY, { align: 'center', maxWidth: cardWidth - 4 });
          }
          
          detailY += 3;
          pdf.text(`Code: ${student.accCode}`, xPosition + cardWidth / 2, detailY, { align: 'center', maxWidth: cardWidth - 4 });
        }

        yPosition += cardHeight + rowSpacing;
        rowsOnPage += 1;
      }

      console.log('PDF generated successfully, saving...');
      pdf.save(`${schoolName.replace(/[^a-zA-Z0-9]/g, '_')}_photobook.pdf`);
      console.log('PDF saved!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <IonPage>
          <IonContent>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
              <CircularProgress />
            </Box>
          </IonContent>
        </IonPage>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <IonPage>
          <IonContent>
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
              <Button startIcon={<ArrowBack />} onClick={handleBack}>Back to Student List</Button>
            </Container>
          </IonContent>
        </IonPage>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
                  Back to Student List
                </Button>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#1d3557" }}>
                  Student Photobook
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {schoolName} - {students.length} students
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<GetApp />}
                onClick={downloadPDF}
                sx={{ bgcolor: "#243b5a", "&:hover": { bgcolor: "#162c47" } }}
              >
                Download PDF
              </Button>
            </Box>

            {/* Photobook Grid */}
            <div ref={printRef}>
              <Grid container spacing={1} data-testid="photobook-grid">
                {students.map((student, index) => (
                  <Grid item xs={12} sm={6} md={3} key={student.id}>
                    <Card sx={{ textAlign: "center", p: 2 }}>
                      <CardContent>
    <Box
  sx={{
    width: 120,
    height: 120,
    mx: "auto",
    mb: 2,
    border: "2px solid #e5e7eb",
    borderRadius: 1,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "#f9fafb"
  }}
>
  <img
    src={
      student.passportBase64 ||
      `${baseUrl()}assets${
        student.passport?.trim()
          ? student.passport
          : `/img/students/${student.studentNumber}.jpg`
      }`
    }
    alt={`${student.firstname} ${student.lastname}`}
    style={{
      width: "100%",
      height: "100%",
      objectFit: "cover"
    }}
    onError={(e) => {
      const target = e.target as HTMLImageElement;
      target.style.display = "none";

      const fallback = document.createElement("div");
      fallback.style.width = "100%";
      fallback.style.height = "100%";
      fallback.style.display = "flex";
      fallback.style.alignItems = "center";
      fallback.style.justifyContent = "center";
      fallback.style.background = "#e5e7eb";
      fallback.style.color = "#6b7280";
      fallback.style.fontSize = "12px";
      fallback.textContent = "No Photo";

      target.parentElement?.appendChild(fallback);
    }}
  />
</Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1d3557" }}>
                          #{student.studentNumber} 
                        </Typography>

                        <Typography variant="body2">
                          {student.lastname} {student.firstname} {student.othername || ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {student.gender}
                        </Typography>
                        {student.dateOfBirth && (
                          <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#6b7280" }}>
                            DOB: {new Date(student.dateOfBirth).toLocaleDateString()}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                          Code: {student.accCode}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </Container>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default PhotobookPage;